// Libraries
import React, { useState, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';

// Styles
import './NotificationContainer.css';

// Types
export interface Notification {
    id: string;
    message: string;
    type: 'success' | 'info' | 'warning' | 'danger';
    duration?: number;
    persistent?: boolean;
    html?: boolean;
}

export interface NotificationContainerHandle {
    addNotification: (message: string, type: Notification['type'], options?: Partial<Notification>) => void;
    removeNotification: (id: string) => void;
    clearAll: () => void;
}

interface NotificationItemProps {
    notification: Notification;
    onRemove: (id: string) => void;
}

// Individual notification component
const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onRemove }) => {
    const [isRemoving, setIsRemoving] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout>();

    const handleRemove = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setIsRemoving(true);
        setTimeout(() => {
            onRemove(notification.id);
        }, 300); // Animation duration
    }, [notification.id, onRemove]);

    // Auto-dismiss logic
    React.useEffect(() => {
        if (!notification.persistent && notification.duration !== 0) {
            const duration = notification.duration || (notification.type === 'danger' ? 20000 : 5000);
            timeoutRef.current = setTimeout(() => {
                handleRemove();
            }, duration);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [notification, handleRemove]);

    const getIcon = () => {
        switch (notification.type) {
            case 'success':
                return 'bi-check-circle-fill';
            case 'info':
                return 'bi-info-circle-fill';
            case 'warning':
                return 'bi-exclamation-triangle-fill';
            case 'danger':
                return 'bi-x-circle-fill';
            default:
                return 'bi-info-circle-fill';
        }
    };

    const getColorClass = () => {
        switch (notification.type) {
            case 'success':
                return 'alert-success';
            case 'info':
                return 'alert-info';
            case 'warning':
                return 'alert-warning';
            case 'danger':
                return 'alert-danger';
            default:
                return 'alert-info';
        }
    };

    const progressBarColor = () => {
        switch (notification.type) {
            case 'success':
                return 'bg-success';
            case 'info':
                return 'bg-info';
            case 'warning':
                return 'bg-warning';
            case 'danger':
                return 'bg-danger';
            default:
                return 'bg-info';
        }
    };

    return (
        <div
            className={`notification-item ${isRemoving ? 'notification-removing' : 'notification-entering'}`}
        >
            <div className={`alert ${getColorClass()} alert-dismissible d-flex align-items-center mb-3 position-relative overflow-hidden`}
                style={{ minWidth: '300px', maxWidth: 'min(600px, 85vw)', wordBreak: 'break-word' }}>

                {/* Progress bar for auto-dismiss */}
                {!notification.persistent && notification.duration !== 0 && (
                    <div
                        className={`position-absolute bottom-0 start-0 ${progressBarColor()} notification-progress`}
                        style={{
                            height: '3px',
                            animationName: 'shrinkWidth',
                            animationDuration: `${notification.duration || (notification.type === 'danger' ? 20000 : 5000)}ms`,
                            width: '100%'
                        }}
                    />
                )}

                {/* Icon */}
                <div className="flex-shrink-0 me-3">
                    <i className={`${getIcon()} fs-5`}></i>
                </div>

                {/* Message */}
                <div className="flex-grow-1">
                    {notification.html ? (
                        <div dangerouslySetInnerHTML={{ __html: notification.message }} />
                    ) : (
                        <span>{notification.message}</span>
                    )}
                </div>

                {/* Close button */}
                <button
                    type="button"
                    className="btn-close flex-shrink-0 ms-2"
                    aria-label="Close"
                    onClick={handleRemove}
                ></button>
            </div>
        </div>
    );
};

// Main notification container component
const NotificationContainer = forwardRef<NotificationContainerHandle>((props, ref) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const addNotification = useCallback((message: string, type: Notification['type'], options: Partial<Notification> = {}) => {
        const id = Math.random().toString(36).substr(2, 9);
        const newNotification: Notification = {
            id,
            message,
            type,
            duration: 5000,
            persistent: false,
            html: false,
            ...options
        };

        setNotifications(prev => [newNotification, ...prev]);

        // Limit to 5 notifications max
        setNotifications(prev => prev.slice(0, 5));
    }, []);

    const removeNotification = useCallback((id: string) => {
        setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, []);

    const clearAll = useCallback(() => {
        setNotifications([]);
    }, []);

    useImperativeHandle(ref, () => ({
        addNotification,
        removeNotification,
        clearAll
    }), [addNotification, removeNotification, clearAll]);

    if (notifications.length === 0) {
        return null;
    }

    return (
        <div
            className="position-fixed top-0 end-0 p-3 notification-container"
            style={{
                zIndex: 9999,
                marginTop: '60px' // Account for navbar height
            }}
        >
            {notifications.map((notification) => (
                <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onRemove={removeNotification}
                />
            ))}

            {/* Clear all button when multiple notifications */}
            {notifications.length > 1 && (
                <div className="text-center mt-2">
                    <button
                        className="btn btn-outline-secondary btn-sm notification-clear-all"
                        onClick={clearAll}
                        style={{ fontSize: '0.75rem' }}
                    >
                        <i className="bi bi-x-circle me-1"></i>
                        Clear All ({notifications.length})
                    </button>
                </div>
            )}
        </div>
    );
});

NotificationContainer.displayName = 'NotificationContainer';

export default NotificationContainer;